import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { bffNdpsSend, getMarketingCode, getOferta } from 'src/bff';
import { findCarline, Loading } from 'src/components';
import { formatPrefix, FormController, removeWhiteSpace, useFaContent, useIsElectrico, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { useGetWaidLitera } from 'src/feature-app/hooks/use-get-waid-litera';
import { formatFlags, formatHeaders, getConfiguradorData, getDpsFields, getOfertasData, useInitializeLeadForm } from 'src/forms';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { CallCenterComercialesScreen } from './CallCenterComercialesScreen';

const myFormName = 'call_center';
export const isCallCenterForm = (formName: string) => formName === myFormName;

export function CallCenterComercialesForm() {
	const { initializeForm } = useOneFormContext();
	const store = useStore();
	const dispatch = useDispatch();
	const faContent = useFaContent();
	const waid = useGetWaidLitera();
	const { dealersInfo } = useSelector((state: OneFormState) => state);
	const { carlines } = dealersInfo;
	const urlParams = new URLSearchParams(window.location.search);
	const [params, setParams] = useState(null);

	useEffect(() => {
		if (urlParams.get('token')) {
			let params = null;

			try {
				const decoded = atob(urlParams.get('token')); // puede fallar si no es Base64 válido
				params = JSON.parse(decoded); // puede fallar si no es JSON válido
				if (params) {
					setParams(params);
				}
			} catch (err) {
				console.error('Error procesando el token:', err);
			}
		}
	}, []);

	useEffect(() => {
		if (params) {
			if (params && params.name) {
				dispatch({ type: 'UPDATE_FIELDS', payload: { name: params.name } });
			}
			if (params && params.prefix && params.prefix.charAt(0) == '+') {
				dispatch({ type: 'UPDATE_FIELDS', payload: { prefix: params.prefix } });
			}
			if (params && params.cellphone) {
				dispatch({ type: 'UPDATE_FIELDS', payload: { cellphone: params.cellphone } });
			}
			if (params && params.ref) {
				dispatch({ type: 'UPDATE_FIELDS', payload: { ref: params.ref } });
			}
			if (params && params.lssi) {
				dispatch({ type: 'UPDATE_FIELDS', payload: { lssi: params.lssi } });
			}
			if (params && params.tmk) {
				dispatch({ type: 'UPDATE_FIELDS', payload: { tmk: params.tmk } });
			}
			if (params && params.carlineCode && carlines) {
				const carline = findCarline(carlines, params.carlineCode);
				if (carline) {
					dispatch({ type: 'UPDATE_FIELDS', payload: { modelo: carline.code } }); // No guardamos name o title por posibles bugs con la palabra "Nuevo"
				}
			}
			if (params && params.ofertaId) {
				getOferta(params.ofertaId).then((oferta) => {
					if (oferta) {
						getMarketingCode(oferta.marketingCode ? oferta.marketingCode : oferta.configuracion.marketingCode).then((res) => {
							dispatch({ type: 'UPDATE_FIELDS', payload: { marketingCodeResponse: res.response } });
						});
						dispatch({ type: 'SET_OFERTA', payload: oferta });
						if (!params.carlineCode && carlines) {
							dispatch({ type: 'SET_SELECTED_CARLINE', payload: findCarline(carlines, oferta.carline.code) });
						}
					}
				});
			}
		}
	}, [params, carlines]);

	const defaultSteps: Steps[] = [
		{
			title: null,
			fields: <CallCenterComercialesScreen />,
			screenIndex: 0,
			name: 'Form',
		},
	];

	const CallCenterComercialFormData: Partial<FormDataGroup> = {
		headers: {
			FORM_NAME: 'vw-oneform-lead-' + myFormName,
			FORM_OBJECTIVE: 'Más Información',
			FORM_ID: waid ? waid : 'B09C01_LEM_WA1',
			DPS_TOKEN: 'LCV_DDB_FRM_LEM_REC_LEM',
		},
		flags: {
			PERSON_RGPD_ADAPTED: 'true',
			FLAG_IS_MYSTERY_LEAD: 'false',
			FLAG_RELACION_MARCA_SIN_IC: 'false',
			FLAG_SF_INTEGRATION: 'true',
		},
		fields: {
			formName: myFormName,
			formType: 'contact',
		}, // Analítica
	};

	const sendForm = (): Promise<boolean> => {
		const { formData, dealersInfo }: OneFormState = store.getState();
		const { fields, flags, headers } = formData;
		const { selectedInstallation, selectedCarline } = dealersInfo;

		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, referalUrl, url, device } = getParamsForm();

			const isArgumentado = fields.estadoLlamada == 'Argumentado';

			const data = {
				...formatHeaders(headers),
				'headers[FORM_URL]': url,
				'headers[ORIGIN_DEVICE_TYPE]': device,
				'headers[FORM_DATE_INSERTION]': today,
				'headers[ORIGIN_TRACKING_CODE]': tc,
				'headers[FORM_VERSION]': `vw-oneform-empresas-${process.env.VERSION}`,
				'headers[FORM_MKT_CHANNEL]': 'Call Center',
				'headers[FORM_DETAILS]': fields.estadoLlamada,

				...formatFlags(flags),
				'flags[FLAG_IS_COMPANY]': false,

				...getDpsFields(faContent),
				'fields[DPS_BRAND]': 'Volkswagen CV',
				'fields[SF_LEAD_RECORDTYPE_ID]': '0121i000000D7NyAAK',
				'fields[SF_LEAD_RECORDTYPE_NAME]': 'Sales Assistant',
				'fields[SF_LEAD_SALES_INITIAL_ACTION]': isArgumentado ? 'Direct to Sales Assistant' : 'Prospect',

				'fields[PERSON_TREATMENT]': fields.trato,
				'fields[PERSON_NAME]': fields.name,
				'fields[PERSON_SURNAME_1]': fields.surname,
				'fields[PERSON_SURNAME_2]': fields.secondSurname,
				'fields[PERSON_DNI_NIF]': fields.dniNif,
				'fields[PERSON_EMAIL]': removeWhiteSpace(fields.email),
				'fields[PERSON_PHONE_MOBILE_FULL]': `${formatPrefix(fields.prefix)}${fields.cellphone}`,
				'fields[PERSON_PHONE_MOBILE]': fields.cellphone,
				'fields[PERSON_PHONE_PREFIX]': formatPrefix(fields.prefix),
				'fields[PERSON_POSTAL_CP]': fields.postalCode,
				'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi ? fields.lssi : 'NO INFORMADO',
				'fields[PERSON_RGPD_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',

				'fields[SF_LEAD_INSTALLATION_ADDRESS]': selectedInstallation.ShippingStreet,
				'fields[SF_LEAD_INSTALLATION_CODE]': selectedInstallation.VGED_DealerCode__c,
				'fields[SF_LEAD_INSTALLATION_ID]': selectedInstallation.Id,
				'fields[SF_LEAD_INSTALLATION_NAME]': selectedInstallation.VGED_CommercialName__c,
				'fields[SF_LEAD_INSTALLATION_PROVINCE_CODE]': selectedInstallation.ShippingStateCode.toString().padStart(2, '0'),

				'fields[VEH_INTEREST_BRAND]': 'Volkswagen CV',
				'fields[VEH_INTEREST_NEW_OR_USED]': 'New Car',
				'fields[VEH_INTEREST_FUEL]': useIsElectrico(selectedCarline ? selectedCarline.title : fields.modeloList?.name) ? 'Electric' : 'Undefined',
				'fields[VEH_INTEREST_ID]': selectedCarline ? selectedCarline.code : fields.modeloList?.code,
				'fields[VEH_INTEREST_MODEL]': selectedCarline ? selectedCarline.title : fields.modeloList?.name,

				'fields[VEH_INTEREST_LINK]': fields.ref,

				...(await getOfertasData(formData, dealersInfo)),
				...(await getConfiguradorData(formData)),
			};

			const { success, response } = await bffNdpsSend(data);

			if (success) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const { loading, steps, firstStep, lastStep } = useInitializeLeadForm(defaultSteps);

	useEffect(() => {
		if (loading) {
			return;
		}

		const CallCenterComercialFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens: steps.length,
			screenIndex: firstStep.screenIndex,
			step: firstStep,
			steps: steps,
			notALayer: true,
			formType: 'inPage',
			formTheme: 'main',
			stepsHistory: [0],
			thankYouComponent: null,
		};

		initializeForm(CallCenterComercialFormInfo, CallCenterComercialFormData);
	}, [loading]);

	return loading ? <Loading /> : <FormController steps={steps} screenType="layer-screen" />;
}
